/**
 * File: AppRouter.tsx
 * Path: apps/admin/src/AppRouter.tsx
 * Description: Finalized central route registry synchronized with the SSOT path configuration.
 */

import { lazy, Suspense, type ComponentType } from "react";
import { AppLayout } from "@/modules/layout/components/AppLayout";
import { PageLoading } from "@/modules/layout/components/PageLoading";
import { DashboardPage } from "@/routes/dashboard/DashboardPage";
import { ProtectedRoute } from "@/modules/auth/components/ProtectedRoute";
import { LoginPage } from "@/routes/LoginPage";
import { SectionMenuPage } from "@/routes/menu/SectionMenuPage";
import { DiagnosticsPage } from "@/routes/diagnostics/DiagnosticsPage";
import { RedirectKeepingSearch } from "@/modules/layout/components/RedirectKeepingSearch";
import { ADMIN_PATHS } from "@idk/config";
import {
  createBrowserRouter,
  Link,
  Navigate,
  Outlet,
  RouterProvider,
  type RouteObject,
} from "react-router-dom";
import { PageError } from "@/modules/app/components/PageError";

/**
 * Pages load on demand, so signing in and opening Today no longer downloads
 * accounting, contracts, the CMS editors and the tools first. The shell
 * (login, layout, sidebar, bell), Today, the phone section menus and
 * Diagnostics stay in the main bundle: Diagnostics is where a failed page
 * sends you, so it must open even when a chunk won't.
 */
const lazyPage = <M, K extends keyof M>(load: () => Promise<M>, name: K) =>
  lazy(async () => ({ default: (await load())[name] as ComponentType }));

const LeadsPipelinePage = lazyPage(() => import("@/routes/leads/LeadsPipelinePage"), "LeadsPipelinePage");
const FindLeadsPage = lazyPage(() => import("@/routes/leads/FindLeadsPage"), "FindLeadsPage");
const BusinessPage = lazyPage(() => import("@/routes/business/BusinessPage"), "BusinessPage");
const LegacyBusinessRedirect = lazyPage(() => import("@/routes/business/LegacyBusinessRedirect"), "LegacyBusinessRedirect");

const ClientsPage = lazyPage(() => import("@/routes/clients/ClientsPage"), "ClientsPage");

const ProjectsPage = lazyPage(() => import("@/routes/projects/ProjectsPage"), "ProjectsPage");
const ProjectDetailPage = lazyPage(() => import("@/routes/projects/ProjectDetailPage"), "ProjectDetailPage");

const QuoteBuilderPage = lazyPage(() => import("@/routes/quotes/QuoteBuilderPage"), "QuoteBuilderPage");
const EstimatePage = lazyPage(() => import("@/routes/quotes/EstimatePage"), "EstimatePage");

const NewContractPage = lazyPage(() => import("@/routes/contracts/NewContractPage"), "NewContractPage");
const BulkImportContractsPage = lazyPage(() => import("@/routes/contracts/BulkImportContractsPage"), "BulkImportContractsPage");
const ImportContractPage = lazyPage(() => import("@/routes/contracts/ImportContractPage"), "ImportContractPage");
const NewInvoicePage = lazyPage(() => import("@/routes/contracts/NewInvoicePage"), "NewInvoicePage");
const CrmSettingsPage = lazyPage(() => import("@/routes/crmSettings/CrmSettingsPage"), "CrmSettingsPage");
const ContractDetailPage = lazyPage(() => import("@/routes/contracts/ContractDetailPage"), "ContractDetailPage");

const MoneyPage = lazyPage(() => import("@/routes/money/MoneyPage"), "MoneyPage");
const InvoicePage = lazyPage(() => import("@/routes/money/InvoicePage"), "InvoicePage");
const CarePlansPage = lazyPage(() => import("@/routes/retainers/CarePlansPage"), "CarePlansPage");
const CarePlanDetailPage = lazyPage(() => import("@/routes/retainers/CarePlanDetailPage"), "CarePlanDetailPage");
const GlossaryPage = lazyPage(() => import("@/routes/GlossaryPage"), "GlossaryPage");
const ReferralsPage = lazyPage(() => import("@/routes/referrals/ReferralsPage"), "ReferralsPage");

const ProjectsListPage = lazyPage(() => import("@/routes/portfolio/ProjectsListPage"), "ProjectsListPage");
const ProjectEditorPage = lazyPage(() => import("@/routes/portfolio/ProjectEditorPage"), "ProjectEditorPage");
const ProjectPreviewPage = lazyPage(() => import("@/routes/portfolio/ProjectPreviewPage"), "ProjectPreviewPage");

const AcademyListPage = lazyPage(() => import("@/routes/academy/AcademyListPage"), "AcademyListPage");
const AcademyEditorPage = lazyPage(() => import("@/routes/academy/AcademyEditorPage"), "AcademyEditorPage");
const AcademyPreviewPage = lazyPage(() => import("@/routes/academy/AcademyPreviewPage"), "AcademyPreviewPage");

const ServicesListPage = lazyPage(() => import("@/routes/services/ServicesListPage"), "ServicesListPage");
const ServiceDetailPage = lazyPage(() => import("@/routes/services/ServiceDetailPage"), "ServiceDetailPage");

const ReviewsPage = lazyPage(() => import("@/routes/reviews/ReviewsPage"), "ReviewsPage");
const SeoOverviewPage = lazyPage(() => import("@/routes/SeoOverviewPage"), "SeoOverviewPage");

const OverviewPage = lazyPage(() => import("@/routes/accounting/OverviewPage"), "OverviewPage");
const JournalsPage = lazyPage(() => import("@/routes/accounting/JournalsPage"), "JournalsPage");
const AccountsPage = lazyPage(() => import("@/routes/accounting/AccountsPage"), "AccountsPage");
const AccountPage = lazyPage(() => import("@/routes/accounting/AccountPage"), "AccountPage");
const BudgetsPage = lazyPage(() => import("@/routes/accounting/BudgetsPage"), "BudgetsPage");
const ReportsPage = lazyPage(() => import("@/routes/accounting/ReportsPage"), "ReportsPage");
const TaxPage = lazyPage(() => import("@/routes/accounting/TaxPage"), "TaxPage");
const IncomeTaxPage = lazyPage(() => import("@/routes/accounting/IncomeTaxPage"), "IncomeTaxPage");
const ProvisionalTaxPage = lazyPage(() => import("@/routes/accounting/ProvisionalTaxPage"), "ProvisionalTaxPage");
const OtherTaxesPage = lazyPage(() => import("@/routes/accounting/OtherTaxesPage"), "OtherTaxesPage");
const CipcPage = lazyPage(() => import("@/routes/accounting/CipcPage"), "CipcPage");
const GuidePage = lazyPage(() => import("@/routes/accounting/GuidePage"), "GuidePage");
const BooksSetupPage = lazyPage(() => import("@/routes/accounting/BooksSetupPage"), "BooksSetupPage");

const AuditToolPage = lazyPage(() => import("@/routes/tools/AuditToolPage"), "AuditToolPage");
const BrandToolPage = lazyPage(() => import("@/routes/tools/BrandToolPage"), "BrandToolPage");

const SettingsPage = lazyPage(() => import("@/routes/settings/SettingsPage"), "SettingsPage");
const NotificationsPage = lazyPage(
  () => import("@/routes/notifications/NotificationsPage"),
  "NotificationsPage",
);

/**
 * A page that crashes shows a plain error inside the admin (sidebar and
 * menu still there) instead of React Router's developer screen, and the
 * error is recorded for System > Diagnostics.
 */
const withPageErrors = (routes: RouteObject[]): RouteObject[] => [
  {
    errorElement: <PageError />,
    // A page still downloading shows a quiet line inside the admin; a chunk
    // that fails to load (offline, or an old tab after a deploy) lands on
    // PageError above, whose Reload fetches the new build.
    element: (
      <Suspense fallback={<PageLoading />}>
        <Outlet />
      </Suspense>
    ),
    children: routes,
  },
];

const router = createBrowserRouter([
  {
    path: "/",
    element: <Navigate to={ADMIN_PATHS.dashboard} replace />,
  },
  {
    path: ADMIN_PATHS.login,
    element: <LoginPage />,
  },
  // Previews render unpublished drafts, so they sit behind the same auth gate
  // as the editors (P2.6). They stay outside AppLayout so a preview still
  // fills the window with no admin chrome around it.
  {
    path: ADMIN_PATHS.portfolio.preview(":id"),
    element: (
      <ProtectedRoute requires="content">
        <ProjectPreviewPage />
      </ProtectedRoute>
    ),
  },
  {
    path: ADMIN_PATHS.academy.preview(":id"),
    element: (
      <ProtectedRoute requires="content">
        <AcademyPreviewPage />
      </ProtectedRoute>
    ),
  },
  {
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: withPageErrors([
      {
        path: ADMIN_PATHS.dashboard,
        element: <DashboardPage />,
      },
      // Phone section pages (CRM, Accounting, CMS), opened from the bottom bar.
      // No ProtectedRoute of its own: the page lists only what the role may
      // open and sends anyone with nothing in the group to the dashboard.
      {
        path: ADMIN_PATHS.menu.crm,
        element: <SectionMenuPage />,
      },
      {
        path: ADMIN_PATHS.menu.accounting,
        element: <SectionMenuPage />,
      },
      {
        path: ADMIN_PATHS.menu.cms,
        element: <SectionMenuPage />,
      },
      // Leads: the pipeline, the Google Maps search, one page per lead
      {
        path: ADMIN_PATHS.leads.root,
        element: (
          <ProtectedRoute requires="sales">
            <LeadsPipelinePage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.leads.find,
        element: (
          <ProtectedRoute requires="sales">
            <FindLeadsPage />
          </ProtectedRoute>
        ),
      },
      // One page per business, lead or client; the old addresses redirect to it
      {
        path: ADMIN_PATHS.businesses.detail(":id"),
        element: (
          <ProtectedRoute requires="sales">
            <BusinessPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.leads.legacyDetail(":id"),
        element: <LegacyBusinessRedirect />,
      },
      {
        path: ADMIN_PATHS.leads.legacy,
        element: <Navigate to={ADMIN_PATHS.leads.root} replace />,
      },
      // Money owed and retainers: what is still to come in, and what pays monthly
      // Numbers now live on Today; the old address (and the glossary) still opens.
      {
        path: ADMIN_PATHS.numbers.root,
        element: <Navigate to={ADMIN_PATHS.dashboard} replace />,
      },
      {
        path: ADMIN_PATHS.numbers.glossary,
        element: (
          <ProtectedRoute requires="sales">
            <GlossaryPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.money,
        element: (
          <ProtectedRoute requires="sales">
            <MoneyPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.invoice(":key"),
        element: (
          <ProtectedRoute requires="sales">
            <InvoicePage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.moneyOwed,
        element: <Navigate to={ADMIN_PATHS.money} replace />,
      },
      {
        path: ADMIN_PATHS.carePlans.root,
        element: (
          <ProtectedRoute requires="sales">
            <CarePlansPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.carePlans.detail(":id"),
        element: (
          <ProtectedRoute requires="sales">
            <CarePlanDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.retainers,
        element: <Navigate to={ADMIN_PATHS.carePlans.root} replace />,
      },
      // Referrals: partners who send work, under CRM
      {
        path: ADMIN_PATHS.referrals.root,
        element: (
          <ProtectedRoute requires="referrals">
            <ReferralsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.referrals.legacy,
        element: <Navigate to={ADMIN_PATHS.referrals.root} replace />,
      },
      // Clients: won work and the case studies made for it
      {
        path: ADMIN_PATHS.clients.root,
        element: (
          <ProtectedRoute requires="sales">
            <ClientsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.clients.legacyDetail(":id"),
        element: <LegacyBusinessRedirect />,
      },
      // Projects: the jobs built for clients, a board by stage, one page each
      {
        path: ADMIN_PATHS.projects.root,
        element: (
          <ProtectedRoute requires="sales">
            <ProjectsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.projects.detail(":clientId", ":jobId"),
        element: (
          <ProtectedRoute requires="sales">
            <ProjectDetailPage />
          </ProtectedRoute>
        ),
      },
      // Quotes: every saved quote, the builder for a new one, and the
      // questionnaire sent as an estimate
      {
        // The list lives on Money > Agreements now.
        path: ADMIN_PATHS.quotes.root,
        element: <Navigate to={`${ADMIN_PATHS.money}?tab=agreements`} replace />,
      },
      {
        path: ADMIN_PATHS.quotes.new,
        element: (
          <ProtectedRoute requires="sales">
            <QuoteBuilderPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.quotes.estimate,
        element: (
          <ProtectedRoute requires="sales">
            <EstimatePage />
          </ProtectedRoute>
        ),
      },
      {
        // Links into the old Tools page carry ?leadId= or ?clientId=.
        path: ADMIN_PATHS.quotes.legacy,
        element: <RedirectKeepingSearch to={ADMIN_PATHS.quotes.new} />,
      },
      // Contracts: drafts from quotes, signing links, countersigning
      {
        // The list lives on Money > Agreements now.
        path: ADMIN_PATHS.contracts.root,
        element: <Navigate to={`${ADMIN_PATHS.money}?tab=agreements`} replace />,
      },
      {
        path: ADMIN_PATHS.contracts.new,
        element: (
          <ProtectedRoute requires="sales">
            <NewContractPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.contracts.importBulk,
        element: (
          <ProtectedRoute requires="sales">
            <BulkImportContractsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.contracts.import,
        element: (
          <ProtectedRoute requires="sales">
            <ImportContractPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.contracts.newInvoice,
        element: (
          <ProtectedRoute requires="sales">
            <NewInvoicePage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.contracts.settings,
        element: <Navigate to={ADMIN_PATHS.crmSettings.payment} replace />,
      },
      {
        path: ADMIN_PATHS.crmSettings.root,
        element: (
          <ProtectedRoute requires="sales">
            <CrmSettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.contracts.detail(":id"),
        element: (
          <ProtectedRoute requires="sales">
            <ContractDetailPage />
          </ProtectedRoute>
        ),
      },
      // Projects Feature
      {
        path: ADMIN_PATHS.portfolio.root,
        element: (
          <ProtectedRoute requires="content">
            <ProjectsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.portfolio.new,
        element: (
          <ProtectedRoute requires="content">
            <ProjectEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.portfolio.edit(":id"),
        element: (
          <ProtectedRoute requires="content">
            <ProjectEditorPage />
          </ProtectedRoute>
        ),
      },

      // Academy Feature
      {
        path: ADMIN_PATHS.academy.root,
        element: (
          <ProtectedRoute requires="content">
            <AcademyListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.academy.new,
        element: (
          <ProtectedRoute requires="content">
            <AcademyEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.academy.edit(":id"),
        element: (
          <ProtectedRoute requires="content">
            <AcademyEditorPage />
          </ProtectedRoute>
        ),
      },
      // Services Feature (read-only registry from @idk/config)
      {
        path: ADMIN_PATHS.services.root,
        element: (
          <ProtectedRoute requires="content">
            <ServicesListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.services.detail(":id"),
        element: (
          <ProtectedRoute requires="content">
            <ServiceDetailPage />
          </ProtectedRoute>
        ),
      },
      // Google reviews, review links, and reviews attached to projects
      {
        path: ADMIN_PATHS.reviews,
        element: (
          <ProtectedRoute requires="content">
            <ReviewsPage />
          </ProtectedRoute>
        ),
      },
      // Search & AI readiness across portfolio and academy
      {
        path: ADMIN_PATHS.seo,
        element: (
          <ProtectedRoute requires="content">
            <SeoOverviewPage />
          </ProtectedRoute>
        ),
      },
      // Accounting: the books, for super admins and admins only
      {
        path: ADMIN_PATHS.accounting.root,
        element: <Navigate to={ADMIN_PATHS.accounting.overview} replace />,
      },
      {
        path: ADMIN_PATHS.accounting.overview,
        element: (
          <ProtectedRoute requires="finance">
            <OverviewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.accounting.journals,
        element: (
          <ProtectedRoute requires="finance">
            <JournalsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.accounting.accounts,
        element: (
          <ProtectedRoute requires="finance">
            <AccountsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: `${ADMIN_PATHS.accounting.accounts}/:accountId`,
        element: (
          <ProtectedRoute requires="finance">
            <AccountPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.accounting.budgets,
        element: (
          <ProtectedRoute requires="finance">
            <BudgetsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.accounting.reports,
        element: (
          <ProtectedRoute requires="finance">
            <ReportsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.accounting.tax,
        element: (
          <ProtectedRoute requires="finance">
            <TaxPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.accounting.taxIncome,
        element: (
          <ProtectedRoute requires="finance">
            <IncomeTaxPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.accounting.taxProvisional,
        element: (
          <ProtectedRoute requires="finance">
            <ProvisionalTaxPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.accounting.taxOther,
        element: (
          <ProtectedRoute requires="finance">
            <OtherTaxesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.accounting.taxCompany,
        element: (
          <ProtectedRoute requires="finance">
            <CipcPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.accounting.cipc,
        element: <Navigate to={ADMIN_PATHS.accounting.taxCompany} replace />,
      },
      {
        path: ADMIN_PATHS.accounting.guide,
        element: (
          <ProtectedRoute requires="finance">
            <GuidePage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.accounting.setup,
        element: (
          <ProtectedRoute requires="finance">
            <BooksSetupPage />
          </ProtectedRoute>
        ),
      },
      // Tools: the website audit (for sites that aren't leads) and the brand kit
      {
        path: ADMIN_PATHS.tools.root,
        element: <Navigate to={ADMIN_PATHS.dashboard} replace />,
      },
      {
        path: ADMIN_PATHS.tools.audit,
        element: (
          <ProtectedRoute requires="tools">
            <AuditToolPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_PATHS.tools.brand,
        element: (
          <ProtectedRoute requires="tools">
            <BrandToolPage />
          </ProtectedRoute>
        ),
      },
      // Settings: every staff role has its profile; Users needs `users`
      {
        path: ADMIN_PATHS.settings,
        element: <SettingsPage />,
      },
      // The bell: every staff role (each event is filtered by role when sent)
      {
        path: ADMIN_PATHS.notifications,
        element: <NotificationsPage />,
      },
      {
        path: ADMIN_PATHS.diagnostics,
        element: <DiagnosticsPage />,
      },
    ]),
  },
  {
    path: "*",
    element: (
      <div className="flex h-screen flex-col items-center justify-center bg-background text-foreground">
        <h1 className="text-9xl font-black tracking-tighter text-secondary">
          404
        </h1>
        <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Invalid Path
        </p>
        <Link
          to="/"
          className="mt-stack-xl text-primary underline underline-offset-8 uppercase text-xs font-bold tracking-wider hover:text-primary/80 transition-colors"
        >
          Back to the dashboard
        </Link>
      </div>
    ),
  },
]);

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
